/**
 * helpGuides.js — Contenido del Centro de Ayuda.
 *
 * Cada guía: { id, topic, title, icon, intro, steps:[{title, body}], cta:{label, view} }
 * `cta.view` es un id de vista del Dashboard (setView), p.ej. 'flows', 'channels'.
 */

export const HELP_GUIDES = [
	{
		id: 'flows',
		topic: 'Automatización',
		title: 'Flujos (Salesbot)',
		icon: 'zap',
		intro: 'Los flujos son conversaciones automáticas: una secuencia de pasos (enviar mensaje, preguntar, decidir, escalar a un humano) que se ejecuta sola cuando llega un mensaje. Ideal para dar la bienvenida, mostrar un menú o calificar leads sin estar pendiente.',
		steps: [
			{ title: 'Empieza con una plantilla', body: 'En la pantalla de Flujos verás "Plantillas para empezar". Haz clic en "Usar" en cualquiera (Bienvenida, Menú de opciones, Calificación de leads, Fuera de horario) y se creará un flujo de ejemplo que puedes editar a tu gusto.' },
			{ title: 'Conoce las 3 áreas del editor', body: 'A la izquierda la Paleta (el trigger y los nodos que puedes agregar), al centro el Lienzo (donde conectas los pasos) y a la derecha el Inspector (donde editas el paso seleccionado).' },
			{ title: 'Elige cuándo se dispara (trigger)', body: 'Conversación nueva (al primer mensaje del cliente), Cada mensaje, o Palabra clave (cuando el cliente escribe algo como "precio"). Lo configuras en la Paleta.' },
			{ title: 'Conecta los pasos', body: 'Arrastra desde el punto de salida de un nodo hacia el siguiente. En un Menú o una Condición, conecta cada salida (cada opción / sí / no) a su paso correspondiente. Un paso sin conectar deja al cliente "colgado".' },
			{ title: 'Usa variables', body: 'Lo que el cliente responde en un paso "Pregunta" se guarda en una variable. Reutilízala escribiendo {{nombre}} en mensajes siguientes. También {{contact.name}} o {{contact.phone}}.' },
			{ title: 'Pruébalo antes de activar', body: 'Usa el panel de prueba del editor: escribe respuestas como lo haría un cliente y verás qué camino toma el flujo. Así detectas opciones mal conectadas.' },
			{ title: 'Borrador vs Activo', body: 'Mientras está en BORRADOR, el flujo NO se ejecuta con clientes reales: solo lo editas y pruebas. Cuando esté listo, ponlo en ACTIVO con el interruptor de arriba (o "Activar ahora" en el banner amarillo). Solo se activa si no tiene errores.' }
		],
		cta: { label: 'Ir a Flujos', view: 'flows' }
	},
	{
		id: 'channels',
		topic: 'Configuración',
		title: 'Conectar canales',
		icon: 'globe',
		intro: 'Los canales son por dónde recibes y envías mensajes: WhatsApp (QR o API oficial), Facebook, Instagram, web chat, etc. Necesitas al menos uno conectado para empezar a conversar.',
		steps: [
			{ title: 'Abre Canales y crea uno', body: 'Haz clic en "Agregar canal" y elige el tipo. Para WhatsApp sin API oficial, elige "WhatsApp QR".' },
			{ title: 'Escanea el código QR', body: 'En WhatsApp QR, abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular dispositivo, y escanea el QR que aparece en pantalla.' },
			{ title: 'Verifica la conexión', body: 'Cuando el estado pase a "Conectado", ya recibes y envías mensajes desde la Bandeja. Si se desconecta, vuelve a escanear el QR.' }
		],
		cta: { label: 'Ir a Canales', view: 'channels' }
	},
	{
		id: 'ai-agents',
		topic: 'Inteligencia Artificial',
		title: 'Agentes IA',
		icon: 'sparkles',
		intro: 'Un Agente IA responde por ti con lenguaje natural, usando tu base de conocimiento. Úsalo para atender 24/7 y dentro de tus flujos (nodo "AI Agent").',
		steps: [
			{ title: 'Crea un agente', body: 'En Agentes IA, define su nombre, personalidad (vendedor, soporte...) e instrucciones de cómo debe responder.' },
			{ title: 'Dale conocimiento', body: 'Conecta tu Base de Conocimiento para que responda con info real de tu negocio (precios, horarios, productos).' },
			{ title: 'Actívalo en un canal o flujo', body: 'Puedes activar respuestas automáticas en un canal, o agregar un nodo "AI Agent" dentro de un flujo para un turno puntual.' }
		],
		cta: { label: 'Ir a Agentes IA', view: 'ai-agents' }
	},
	{
		id: 'inbox',
		topic: 'Día a día',
		title: 'La Bandeja',
		icon: 'inbox',
		intro: 'La Bandeja es donde tú y tu equipo atienden las conversaciones de todos los canales en un solo lugar.',
		steps: [
			{ title: 'Responde conversaciones', body: 'Selecciona una conversación y escribe. Puedes enviar texto, imágenes y archivos adjuntos.' },
			{ title: 'Asigna y etiqueta', body: 'Asigna la conversación a un agente y usa etiquetas para organizar (ej. "Lead", "Soporte", "VIP").' },
			{ title: 'Crea un lead desde el chat', body: 'Desde el panel del chat puedes convertir un contacto en lead del CRM y moverlo por las etapas.' }
		],
		cta: { label: 'Ir a la Bandeja', view: 'conversations' }
	},
	{
		id: 'leads',
		topic: 'CRM',
		title: 'Leads y pipeline',
		icon: 'kanban',
		intro: 'El tablero de Leads (Kanban) te deja seguir tus oportunidades por etapas: Nuevo, Contactado, Calificado, Ganado, Perdido.',
		steps: [
			{ title: 'Crea o recibe leads', body: 'Agrégalos manualmente, desde una conversación, o automáticamente con un flujo de calificación.' },
			{ title: 'Arrástralos por etapas', body: 'Mueve cada tarjeta entre columnas según avanza el trato. El total y el valor se actualizan solos.' },
			{ title: 'Filtra Activos / Todos', body: 'Por defecto ves los leads activos. Cambia a "Todos" para ver también los Ganados y Perdidos (con su badge de color).' }
		],
		cta: { label: 'Ir a Leads', view: 'leads' }
	}
];
